import React, { useCallback } from 'react';

import {
  Autocomplete,
  Box,
  Chip,
  FormControl,
  GridLegacy as Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';

import { RuleCriteria } from '~/services/AccommodationService';

import { parseStrictInteger } from '~/utils/valueParsers';

import { ALL_BOARD_CODES } from '../Bundles/constants';

const ALL_REFUNDABLE_STATUSES: Array<NonNullable<RuleCriteria['refundableStatuses']>[number]> = [
  'refundable',
  'partially',
  'non-refundable',
];

const DAY_OF_WEEK_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

interface StringListFieldProps {
  label: string;
  helperText?: string;
  value: Array<string>;
  onChange: (next: Array<string>) => void;
  options?: Array<string>;
}

// Generic freeSolo chip list for the plain string[] criteria fields (regions, suppliers, ids...).
function StringListField({ label, helperText, value, onChange, options = [] }: StringListFieldProps) {
  return (
    <Autocomplete
      multiple
      freeSolo
      size="small"
      options={options.filter((o) => !value.includes(o))}
      value={value}
      onChange={(_event, next) => onChange(next)}
      renderTags={(items, getTagProps) =>
        items.map((item, index) => <Chip {...getTagProps({ index })} key={item} label={item} size="small" />)
      }
      renderInput={(params) => (
        <TextField {...params} label={label} helperText={helperText} placeholder="Type and press Enter" />
      )}
    />
  );
}

// Mirrors svc-accommodation's hasAnyCriteria (src/shared/models/postgres/Rule.ts) so the Save
// button can be disabled client-side instead of round-tripping to discover an empty rule is invalid.
export function hasAnyCriteria(criteria: RuleCriteria): boolean {
  const isSet = (v: number | undefined) => v !== undefined;
  const isPositive = (v: number | undefined) => v !== undefined && v > 0;
  const isNonEmpty = (v: Array<unknown> | undefined) => !!v && v.length > 0;

  return (
    isNonEmpty(criteria.boardCodes) ||
    isNonEmpty(criteria.refundableStatuses) ||
    isPositive(criteria.minLengthOfStay) ||
    isPositive(criteria.minLeadDays) ||
    isNonEmpty(criteria.ratePlanIds) ||
    isNonEmpty(criteria.roomIds) ||
    isNonEmpty(criteria.supplierRateIds) ||
    isNonEmpty(criteria.suppliers) ||
    isNonEmpty(criteria.internalServices) ||
    isNonEmpty(criteria.regions) ||
    isNonEmpty(criteria.checkInDaysOfWeek) ||
    isSet(criteria.occupancy?.adults) ||
    isSet(criteria.occupancy?.children) ||
    isSet(criteria.occupancy?.infants) ||
    isPositive(criteria.occupancy?.minAdults) ||
    isPositive(criteria.occupancy?.minChildren) ||
    isPositive(criteria.occupancy?.minInfants)
  );
}

interface Props {
  value: RuleCriteria;
  onChange: (next: RuleCriteria) => void;
}

// Editor for the shared `Rule` criteria shape — not campaign-specific, so it can be reused
// wherever a `Rule` is attached (bundle hotel rules, dynamic pricing, ...).
function RuleCriteriaEditor({ value, onChange }: Props) {
  const updateField = useCallback(
    <K extends keyof RuleCriteria>(key: K, next: RuleCriteria[K] | undefined) => {
      const { [key]: _removed, ...rest } = value;
      onChange(next === undefined ? rest : { ...rest, [key]: next });
    },
    [value, onChange],
  );

  const updateOccupancyField = useCallback(
    (key: keyof NonNullable<RuleCriteria['occupancy']>, next: number | undefined) => {
      const occupancy = { ...value.occupancy };
      if (next === undefined) {
        delete occupancy[key];
      } else {
        occupancy[key] = next;
      }
      updateField('occupancy', Object.keys(occupancy).length > 0 ? occupancy : undefined);
    },
    [value.occupancy, updateField],
  );

  return (
    <Grid container spacing={2}>
      <Grid item xs={6}>
        <TextField
          label="Min length of stay (nights)"
          type="number"
          helperText="Leave blank for no minimum"
          value={value.minLengthOfStay ?? ''}
          onChange={(e) => updateField('minLengthOfStay', parseStrictInteger(e.target.value, 1))}
          inputProps={{ min: 1 }}
          fullWidth
        />
      </Grid>
      <Grid item xs={6}>
        <TextField
          label="Min lead days"
          type="number"
          helperText="Leave blank for no minimum"
          value={value.minLeadDays ?? ''}
          onChange={(e) => updateField('minLeadDays', parseStrictInteger(e.target.value, 1))}
          inputProps={{ min: 1 }}
          fullWidth
        />
      </Grid>

      <Grid item xs={12}>
        <StringListField
          label="Board codes"
          helperText="Leave empty to allow any board code"
          value={value.boardCodes ?? []}
          onChange={(next) => updateField('boardCodes', next.length > 0 ? next : undefined)}
          options={ALL_BOARD_CODES}
        />
      </Grid>

      <Grid item xs={12}>
        <FormControl fullWidth size="small">
          <InputLabel>Refundable statuses</InputLabel>
          <Select
            multiple
            label="Refundable statuses"
            value={value.refundableStatuses ?? []}
            onChange={(e) => {
              const next = e.target.value as RuleCriteria['refundableStatuses'];
              updateField('refundableStatuses', next && next.length > 0 ? next : undefined);
            }}
          >
            {ALL_REFUNDABLE_STATUSES.map((status) => (
              <MenuItem key={status} value={status}>
                {status}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      <Grid item xs={12}>
        <FormControl fullWidth size="small">
          <InputLabel>Check-in days of week</InputLabel>
          <Select
            multiple
            label="Check-in days of week"
            value={value.checkInDaysOfWeek ?? []}
            onChange={(e) => {
              const next = e.target.value as Array<number>;
              updateField('checkInDaysOfWeek', next.length > 0 ? next : undefined);
            }}
            renderValue={(selected) => selected.map((d) => DAY_OF_WEEK_LABELS[d]).join(', ')}
          >
            {DAY_OF_WEEK_LABELS.map((label, day) => (
              <MenuItem key={day} value={day}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      <Grid item xs={12}>
        <StringListField
          label="Regions"
          helperText="@luxuryescapes/lib-regions region codes, e.g. AU, NZ. Leave empty to allow any region"
          value={value.regions ?? []}
          onChange={(next) => updateField('regions', next.length > 0 ? next : undefined)}
        />
      </Grid>

      <Grid item xs={12}>
        <StringListField
          label="Suppliers"
          helperText="Leave empty to allow any supplier"
          value={value.suppliers ?? []}
          onChange={(next) => updateField('suppliers', next.length > 0 ? next : undefined)}
        />
      </Grid>

      <Grid item xs={12}>
        <StringListField
          label="Internal services"
          helperText="Leave empty to allow any internal service"
          value={value.internalServices ?? []}
          onChange={(next) => updateField('internalServices', next.length > 0 ? next : undefined)}
        />
      </Grid>

      <Grid item xs={12}>
        <StringListField
          label="Rate plan IDs"
          value={value.ratePlanIds ?? []}
          onChange={(next) => updateField('ratePlanIds', next.length > 0 ? next : undefined)}
        />
      </Grid>

      <Grid item xs={12}>
        <StringListField
          label="Room IDs"
          value={value.roomIds ?? []}
          onChange={(next) => updateField('roomIds', next.length > 0 ? next : undefined)}
        />
      </Grid>

      <Grid item xs={12}>
        <StringListField
          label="Supplier rate IDs"
          value={value.supplierRateIds ?? []}
          onChange={(next) => updateField('supplierRateIds', next.length > 0 ? next : undefined)}
        />
      </Grid>

      <Grid item xs={12}>
        <Box border={1} borderColor="divider" borderRadius={1} p={2}>
          <Typography variant="subtitle2" mb={1}>
            Occupancy
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Maximum fields (including 0) restrict eligibility; minimum fields only restrict when greater than 0.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <TextField
                label="Max adults"
                type="number"
                helperText="Leave blank for no maximum"
                value={value.occupancy?.adults ?? ''}
                onChange={(e) => updateOccupancyField('adults', parseStrictInteger(e.target.value, 0))}
                inputProps={{ min: 0 }}
                fullWidth
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                label="Max children"
                type="number"
                helperText="Leave blank for no maximum"
                value={value.occupancy?.children ?? ''}
                onChange={(e) => updateOccupancyField('children', parseStrictInteger(e.target.value, 0))}
                inputProps={{ min: 0 }}
                fullWidth
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                label="Max infants"
                type="number"
                helperText="Leave blank for no maximum"
                value={value.occupancy?.infants ?? ''}
                onChange={(e) => updateOccupancyField('infants', parseStrictInteger(e.target.value, 0))}
                inputProps={{ min: 0 }}
                fullWidth
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                label="Min adults"
                type="number"
                helperText="Leave blank for no minimum"
                value={value.occupancy?.minAdults ?? ''}
                onChange={(e) => updateOccupancyField('minAdults', parseStrictInteger(e.target.value, 1))}
                inputProps={{ min: 1 }}
                fullWidth
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                label="Min children"
                type="number"
                helperText="Leave blank for no minimum"
                value={value.occupancy?.minChildren ?? ''}
                onChange={(e) => updateOccupancyField('minChildren', parseStrictInteger(e.target.value, 1))}
                inputProps={{ min: 1 }}
                fullWidth
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                label="Min infants"
                type="number"
                helperText="Leave blank for no minimum"
                value={value.occupancy?.minInfants ?? ''}
                onChange={(e) => updateOccupancyField('minInfants', parseStrictInteger(e.target.value, 1))}
                inputProps={{ min: 1 }}
                fullWidth
              />
            </Grid>
          </Grid>
        </Box>
      </Grid>
    </Grid>
  );
}

export default RuleCriteriaEditor;
